import React from 'react';
import { MenuProduct } from '../services/MenuService';
import ProductTileV3 from './ProductTileV3.tsx';
import { getRecommendedProducts } from '../utils/productRecommendations.ts';
import './YouMightLike.css';

interface YouMightLikeProps {
  currentProduct: MenuProduct;
  allProducts: MenuProduct[];
  menuId?: string;
  pricingType: 'MEDICAL' | 'RECREATIONAL';
  cart: any;
  cartLoading: boolean;
  addingToCart: Set<string>;
  activeMarquees: Set<string>;
  stoppingMarquees: Set<string>;
  dealDescriptions?: Record<string, { title: string; description: string }>;
  productTileCursorStyle?: React.CSSProperties;
  isMobile?: boolean;
  selectedTerpenes?: string[];
  selectedCannabinoids?: string[];
  onProductClick: (product: MenuProduct) => void;
  onProductHover: (productId: string) => void;
  onProductLeave: (productId: string) => void;
  onAddToCart: (product: MenuProduct, variant: any) => void;
  onDebugClick: (product: MenuProduct) => void;
  onDealClick: (product: MenuProduct) => void;
  onVermontIncrement?: (product: MenuProduct, variant: any) => void;
  onVermontDecrement?: (product: MenuProduct, variant: any) => void;
}

const YouMightLike: React.FC<YouMightLikeProps> = ({
  currentProduct,
  allProducts,
  menuId,
  pricingType,
  cart,
  cartLoading,
  addingToCart,
  activeMarquees,
  stoppingMarquees,
  dealDescriptions,
  productTileCursorStyle,
  isMobile: isMobileProp,
  selectedTerpenes = [],
  selectedCannabinoids = [],
  onProductClick,
  onProductHover,
  onProductLeave,
  onAddToCart,
  onDebugClick,
  onDealClick,
  onVermontIncrement,
  onVermontDecrement
}) => {
  // Determine number of products to show based on viewport width
  const [productLimit, setProductLimit] = React.useState(4);
  // Detect mobile internally (matches grid breakpoint at 700px)
  const [isMobileLocal, setIsMobileLocal] = React.useState(window.innerWidth <= 700);
  const isMobile = isMobileProp || isMobileLocal;

  React.useEffect(() => {
    const updateLayout = () => {
      const width = window.innerWidth;
      // Update product limit based on columns
      if (width >= 1200) {
        setProductLimit(4); // Desktop: 4 columns
      } else if (width >= 1135) {
        setProductLimit(3); // Tablet: 3 columns
      } else if (width >= 701) {
        setProductLimit(2); // 2 columns (701px - 1134px)
      } else {
        setProductLimit(4); // Mobile: 1 column, 4 products stacked
      }
      // Update mobile detection (matches grid breakpoint)
      setIsMobileLocal(width <= 700);
    };

    // Set initial values
    updateLayout();

    // Update on resize
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  // Get recommended products using our utility function with dynamic limit and cart
  const recommendedProducts = getRecommendedProducts(currentProduct, allProducts, productLimit, cart);

  // Don't render if we don't have enough recommendations
  if (recommendedProducts.length === 0) {
    return null;
  }

  // Check if we're in Vermont store
  const isVermontStore = menuId === process.env.REACT_APP_RETAILER_ID_ESSEX_JUNCTION_REC;

  // Wrap onProductClick to scroll to top of drawer
  const handleProductClickWithScroll = (product: MenuProduct) => {
    // Call the original handler
    onProductClick(product);

    // Smooth scroll to top of the drawer
    const drawer = document.querySelector('.product-detail-drawer');
    if (drawer) {
      drawer.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="you-might-like-section">
      <h2 className="you-might-like-header">You Might Also Like</h2>
      <div className="you-might-like-grid">
        {recommendedProducts.map((product) => {
          // Get first variant for the product (matching DutchieMenu logic)
          const variant = product.variants?.[0] || null;

          return (
            <ProductTileV3
              key={product.id}
              product={product}
              variant={variant}
              animationClass=""
              pricingType={pricingType}
              menuId={menuId}
              isVermontStore={isVermontStore}
              debugMode={false}
              cart={cart}
              cartLoading={cartLoading}
              isAddingToCart={addingToCart.has(`${product.id}-${variant?.id}`) || addingToCart.has(`vermont-${product.id}`)}
              activeMarquees={activeMarquees}
              stoppingMarquees={stoppingMarquees}
              dealDescriptions={dealDescriptions}
              productTileCursorStyle={productTileCursorStyle}
              isMobile={isMobile}
              isCompactMode={true}
              selectedTerpenes={selectedTerpenes}
              selectedCannabinoids={selectedCannabinoids}
              onProductClick={handleProductClickWithScroll}
              onProductHover={onProductHover}
              onProductLeave={onProductLeave}
              onAddToCart={onAddToCart}
              onDebugClick={onDebugClick}
              onDealClick={onDealClick}
              onVermontIncrement={onVermontIncrement}
              onVermontDecrement={onVermontDecrement}
            />
          );
        })}
      </div>
    </div>
  );
};

export default YouMightLike;